import React, { useState, useEffect, ReactNode, ErrorInfo } from 'react';
import Layout from './components/Layout';
import SurveyForm from './components/SurveyForm';
import Dashboard from './components/Dashboard';
import CardGenerator from './components/CardGenerator';
import AdminManager from './components/AdminManager';
import MySubmissions from './components/MySubmissions';
import QuestionManager from './components/QuestionManager';
import { ToastProvider } from './components/Toast';
import { User, UserRole } from './types';
import { initStorage, loginUser, logoutUser, registerMasterAdmin, getEmail } from './services/storage';
import { auth } from './services/firebase';
import { X } from 'lucide-react';

interface ErrorBoundaryProps {
  children?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

// Error Boundary Component
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public state: ErrorBoundaryState;
  public props: ErrorBoundaryProps;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
    this.props = props;
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
          <div className="bg-white p-8 rounded-lg shadow-xl max-w-lg w-full">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Something went wrong</h2>
            <p className="text-gray-600 mb-4">The application encountered an unexpected error.</p>
            <pre className="bg-gray-100 p-4 rounded text-sm overflow-auto mb-4 border border-gray-300">
              {this.state.error?.message}
            </pre>
            <button 
              onClick={() => window.location.reload()}
              className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700"
            >
              Reload Application
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Login Modal Component (Simplified)
const LoginModal = ({ 
  isOpen,
  onClose,
  onLogin
}: { 
  isOpen: boolean,
  onClose: () => void,
  onLogin: (u: string, p: string) => Promise<boolean>
}) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const success = await onLogin(username.trim(), password);
      if (!success) setError('Invalid credentials');
      else {
        setUsername('');
        setPassword('');
        onClose();
      }
    } catch (err: any) {
      const msg = err.message || 'Action failed';
      if (msg.includes('auth/invalid-credential') || msg.includes('invalid-credential') || msg.includes('configuration-not-found')) {
        setError('Login failed. Please check credentials or contact admin.');
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-sm relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
          <X size={20} />
        </button>
        
        <h2 className="text-xl font-bold text-center text-gray-800 mb-6">Admin Login</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Username / Email</label>
            <input 
              type="text" 
              className="w-full border border-gray-300 rounded-lg p-2.5 focus:ring-2 focus:ring-emerald-500 outline-none" 
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoFocus
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Password</label>
            <input 
              type="password" 
              className="w-full border border-gray-300 rounded-lg p-2.5 focus:ring-2 focus:ring-emerald-500 outline-none" 
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 p-2 rounded text-xs">
              {error}
            </div>
          )}

          <button 
            type="submit" 
            disabled={loading}
            className={`w-full bg-emerald-600 text-white py-2.5 rounded-lg hover:bg-emerald-700 transition-all font-bold shadow-md ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
          >
            {loading ? 'Authenticating...' : 'Login'}
          </button>
        </form>
      </div>
    </div>
  );
};

// Default Guest User Object
const GUEST_USER: User = {
  id: 'guest_session',
  name: 'Guest User',
  role: UserRole.GUEST,
  username: 'guest',
  visibleSections: ['sectionA', 'sectionB']
};

const App: React.FC = () => {
  // Initialize with GUEST_USER so the app opens immediately to survey
  const [user, setUser] = useState<User>(GUEST_USER);
  const [currentView, setCurrentView] = useState('survey');
  const [showLoginModal, setShowLoginModal] = useState(false);

  // Check for persistent auth
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
      if (firebaseUser) {
        try {
           // Silent auto-login logic if needed, usually managed via loginUser's persistence
        } catch (e) {
           console.error("Auto-login failed", e);
        }
      }
    });
    return () => unsubscribe();
  }, []);

  const handleLogin = async (u: string, p: string) => {
    try {
      const foundUser = await loginUser(u, p);
      if (foundUser) {
        setUser(foundUser);
        setCurrentView(foundUser.role === UserRole.REPRESENTATIVE ? 'survey' : 'dashboard');
        initStorage().catch(err => console.warn("Init storage background check failed:", err));
        return true;
      }
      return false;
    } catch (e: any) {
      // Auto-bootstrap logic
      const targetEmail = 'shabeebkk10@gmail.com';
      const computedEmail = getEmail(u);

      if (computedEmail === targetEmail) {
         try {
           const newUser = await registerMasterAdmin('Mahallu Admin', targetEmail, p);
           setUser(newUser);
           setCurrentView('dashboard');
           initStorage().catch(console.error);
           return true;
         } catch (regError: any) {
           if (regError.message && (regError.message.includes('already exists') || regError.message.includes('email-already-in-use'))) {
              throw new Error("Invalid credentials");
           }
           throw regError;
         }
      }
      throw e;
    }
  };

  const handleLogout = async () => {
    if (user.role !== UserRole.GUEST) {
      await logoutUser();
    }
    // Revert to Guest
    setUser(GUEST_USER);
    setCurrentView('survey');
  };

  const renderContent = () => {
    switch (currentView) {
      case 'dashboard':
        return (user.role === UserRole.ADMIN || user.role === UserRole.MASTER_ADMIN) 
          ? <Dashboard user={user} /> 
          : <div className="text-red-500 p-8">Access Denied</div>;
      case 'survey':
        return <SurveyForm 
          user={user} 
          onSuccess={() => {
            if (user.role === UserRole.GUEST) {
              alert("നന്ദി! നിങ്ങളുടെ വിവരങ്ങൾ വിജയകരമായി സമർപ്പിച്ചു. (Thank you! Submitted successfully.)");
              window.location.reload(); 
            } else {
              setCurrentView(user.role === UserRole.REPRESENTATIVE ? 'my-submissions' : 'dashboard');
            }
          }} 
        />;
      case 'my-submissions':
        return user.role === UserRole.REPRESENTATIVE ? <MySubmissions user={user} /> : <div className="text-red-500">Access Denied</div>;
      case 'card-gen':
        return user.role === UserRole.MASTER_ADMIN ? <CardGenerator /> : <div className="text-red-500 p-8">Access Denied</div>;
      case 'admin-users':
        return user.role === UserRole.MASTER_ADMIN ? <AdminManager currentUser={user} /> : <div className="text-red-500 p-8">Access Denied</div>;
      case 'settings':
        return user.role === UserRole.MASTER_ADMIN ? <QuestionManager /> : <div className="text-red-500 p-8">Access Denied</div>;
      default:
        return <div className="text-center text-gray-500 mt-10">Select an option from the menu</div>;
    }
  };

  return (
    <ErrorBoundary>
      <ToastProvider>
        <Layout 
          user={user} 
          onLogout={handleLogout} 
          onNavigate={setCurrentView} 
          currentView={currentView}
          onLoginClick={() => setShowLoginModal(true)}
        >
          {renderContent()}
        </Layout>
        
        <LoginModal 
          isOpen={showLoginModal} 
          onClose={() => setShowLoginModal(false)} 
          onLogin={handleLogin}
        />
      </ToastProvider>
    </ErrorBoundary>
  );
};

export default App;